import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Key,
  Eye,
  Check,
  Calendar,
  Activity,
  Save,
  ArrowRight,
  X,
  List,
  LayoutGrid,
  Download,
} from "lucide-react";
import {
  api,
  apiError,
  type Ticket as TicketType,
  type Priority,
  type UserSummary,
} from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { GlassCard } from "../components/ui/GlassCard";
import { Modal } from "../components/ui/Modal";
import { Spinner } from "../components/ui/FullScreenLoader";
import { Avatar } from "../components/ui/Avatar";
import { PriorityBadge } from "../components/tickets/PriorityBadge";
import { StatusBadge } from "../components/tickets/StatusBadge";
import { CategoryBadge } from "../components/tickets/CategoryBadge";
import { fmtDateTime, fmtDate, truncate } from "../lib/format";
import { PRIORITIES, STATUSES, STATUS_META, PRIORITY_META } from "../lib/constants";
import { cn } from "../lib/cn";
import { exportTicketPdf } from "../lib/exportTicketPdf";

interface LocationState {
  title?: string;
  description?: string;
  priority?: Priority;
}

interface ListViewProps {
  items: TicketType[];
  onSelect: (t: TicketType) => void;
  onResolve: (e: React.MouseEvent, t: TicketType) => void;
  canModify: (t: TicketType) => boolean;
}

function ListView({ items, onSelect, onResolve, canModify }: ListViewProps) {
  return (
    <GlassCard padded={false}>
      {items.length > 0 && (
        <div className="hidden lg:grid grid-cols-12 gap-4 px-6 py-3 bg-white/[0.02] border-b border-white/5 text-[10px] uppercase tracking-wider text-slate-500 font-bold select-none">
          <div className="col-span-2">Ticket ID</div>
          <div className="col-span-3">Issue Details</div>
          <div className="col-span-2">Agent With Issue</div>
          <div className="col-span-2">Priority & Category</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1">Agent Resolve</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
      )}
      <div className="divide-y divide-white/5">
        {items.length === 0 ? (
          <div className="text-sm text-slate-500 italic p-8 text-center">
            No access issues found.
          </div>
        ) : (
          items.map((t) => {
            const isResolved = ["RESOLVED", "CLOSED"].includes(t.status);
            return (
              <div
                key={t.id}
                onClick={() => onSelect(t)}
                className="grid grid-cols-12 gap-4 items-center px-6 py-4 hover:bg-white/[0.03] transition-colors cursor-pointer text-left"
              >
                <div className="col-span-12 lg:col-span-2 flex items-center">
                  <span className="font-mono text-xs text-slate-400 bg-white/5 px-2 py-1 rounded border border-white/10">
                    {t.id.slice(-8).toUpperCase()}
                  </span>
                </div>
                <div className="col-span-12 lg:col-span-3 min-w-0">
                  <div className="text-sm font-semibold text-white truncate hover:text-brand-300 transition-colors">
                    {t.title}
                  </div>
                  <div className="text-xs text-slate-400 truncate mt-0.5">
                    {truncate(t.description, 100)}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1 select-none">
                    {fmtDate(t.createdAt)}
                  </div>
                </div>

                <div className="col-span-12 sm:col-span-4 lg:col-span-2 min-w-0">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold select-none mb-1">
                    Agent with Issue
                  </div>
                  <div className="flex items-center gap-2">
                    <Avatar name={t.submitter?.name || "?"} url={t.submitter?.avatarUrl} size="xs" />
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-slate-200 truncate">
                        {t.submitter?.name || "Unknown"}
                      </div>
                      {t.submitter?.department && (
                        <div className="text-[10px] text-slate-500 truncate">
                          {t.submitter.department}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="col-span-6 sm:col-span-3 lg:col-span-2 flex items-center gap-1.5 flex-wrap">
                  <PriorityBadge priority={t.priority} />
                  <CategoryBadge category={t.category} />
                </div>

                <div className="col-span-6 sm:col-span-2 lg:col-span-1">
                  <StatusBadge status={t.status} />
                </div>

                <div className="col-span-12 sm:col-span-3 lg:col-span-1 min-w-0">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold select-none mb-1">
                    Agent Resolve
                  </div>
                  {t.assignee ? (
                    <div className="flex items-center gap-2">
                      <Avatar name={t.assignee.name} url={t.assignee.avatarUrl} size="xs" />
                      <span className="text-xs text-slate-300 font-medium truncate">
                        {t.assignee.name}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500 italic">Unassigned</span>
                  )}
                </div>

                <div className="col-span-12 sm:col-span-2 lg:col-span-1 flex lg:justify-end select-none">
                  {isResolved ? (
                    <div
                      className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold rounded-xl flex items-center gap-1.5"
                      title={t.resolution || "Resolved"}
                    >
                      <Check className="w-3.5 h-3.5" /> Resolved
                    </div>
                  ) : canModify(t) ? (
                    <button
                      onClick={(e) => onResolve(e, t)}
                      className="px-2.5 py-1 bg-white/5 hover:bg-emerald-500/10 border border-white/10 hover:border-emerald-500/30 text-slate-300 hover:text-emerald-400 text-[10px] rounded-xl flex items-center gap-1 font-semibold transition-all shadow-sm"
                    >
                      <Check className="w-3 h-3 text-slate-400" /> Mark Resolved
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
      </div>
    </GlassCard>
  );
}

export default function IssueAccessTracker() {
  const { user, isIt } = useAuth();

  const canModifyTicket = (ticket: TicketType | null) => {
    if (!ticket) return false;
    if (isIt) return true;
    return false;
  };
  const qc = useQueryClient();
  const location = useLocation();

  const [search, setSearch] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [view, setView] = useState<"list" | "kanban">("list");

  const [isResolveOpen, setIsResolveOpen] = useState(false);
  const [resolvingTicket, setResolvingTicket] = useState<TicketType | null>(null);
  const [resolution, setResolution] = useState("");

  const [isNewOpen, setIsNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newPriority, setNewPriority] = useState<Priority>("MEDIUM");

  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [detailTicket, setDetailTicket] = useState<TicketType | null>(null);
  const [detailResolution, setDetailResolution] = useState("");

  useEffect(() => {
    const state = location.state as LocationState | null;
    if (state) {
      if (state.title) setNewTitle(state.title);
      if (state.description) setNewDesc(state.description);
      if (state.priority) setNewPriority(state.priority);
      setIsNewOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const ticketsQ = useQuery({
    queryKey: ["tickets.access"],
    queryFn: async () =>
      (await api.get("/api/tickets", { params: { category: "ACCESS", pageSize: 100 } })).data,
  });

  const assigneesQ = useQuery({
    queryKey: ["users.assignees"],
    queryFn: async () => {
      const items = (await api.get<{ items: UserSummary[] }>("/api/users/assignees")).data.items;
      return items.filter((u) => u.role === "IT" || u.role === "ADMIN");
    },
  });

  const createMut = useMutation({
    mutationFn: async (data: Record<string, unknown>) =>
      (await api.post("/api/tickets", { ...data, category: "ACCESS" })).data.ticket,
    onSuccess: () => {
      toast.success("System issue created successfully");
      setIsNewOpen(false);
      setNewTitle("");
      setNewDesc("");
      setNewPriority("MEDIUM");
      ticketsQ.refetch();
      qc.invalidateQueries({ queryKey: ["dashboard.recent"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const patchMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      (await api.patch(`/api/tickets/${id}`, data)).data.ticket,
    onSuccess: (updated: TicketType) => {
      toast.success("System issue updated");
      ticketsQ.refetch();
      qc.invalidateQueries({ queryKey: ["dashboard.recent"] });
      if (detailTicket && detailTicket.id === updated.id) {
        setDetailTicket(updated);
      }
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const handleResolveSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!resolution.trim()) {
      toast.error("Please enter a resolution");
      return;
    }
    patchMut.mutate(
      {
        id: resolvingTicket!.id,
        data: {
          status: "RESOLVED",
          resolution: resolution.trim(),
          assigneeId: resolvingTicket!.assigneeId || user?.id,
        },
      },
      {
        onSuccess: () => {
          setIsResolveOpen(false);
          setResolution("");
          setResolvingTicket(null);
        },
      },
    );
  };

  const handleNewSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim() || !newDesc.trim()) {
      toast.error("Please fill out all required fields");
      return;
    }
    createMut.mutate({
      title: newTitle.trim(),
      description: newDesc.trim(),
      priority: newPriority,
      submitterId: user?.id,
    });
  };

  const handleUpdateStatus = (status: string) => {
    if (!detailTicket) return;
    patchMut.mutate({ id: detailTicket.id, data: { status } });
  };

  const handleUpdatePriority = (priority: string) => {
    if (!detailTicket) return;
    patchMut.mutate({ id: detailTicket.id, data: { priority } });
  };

  const handleUpdateAssignee = (assigneeId: string) => {
    if (!detailTicket) return;
    patchMut.mutate({ id: detailTicket.id, data: { assigneeId: assigneeId || null } });
  };

  const handleSaveResolutionInDetail = () => {
    if (!detailTicket) return;
    patchMut.mutate(
      {
        id: detailTicket.id,
        data: { resolution: detailResolution.trim() || null },
      },
      { onSuccess: () => toast.success("Resolution updated") },
    );
  };

  const items: TicketType[] = ticketsQ.data?.items || [];

  const filteredItems = items.filter((t) => {
    const matchesSearch =
      !search ||
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.description.toLowerCase().includes(search.toLowerCase());
    const matchesPriority = !priorityFilter || t.priority === priorityFilter;
    return matchesSearch && matchesPriority;
  });

  const columns = STATUSES.reduce(
    (acc, s) => {
      acc[s] = filteredItems.filter((t) => t.status === s);
      return acc;
    },
    {} as Record<string, TicketType[]>,
  );

  const isLoading = ticketsQ.isLoading;

  const canModifyDetail = canModifyTicket(detailTicket);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-brand-500 to-critical-500 flex items-center justify-center shadow-glow">
              <Key className="w-5 h-5 text-white" />
            </div>
            <span className="bg-gradient-to-r from-white via-slate-100 to-slate-400 bg-clip-text text-transparent">
              Issue Access Tracker
            </span>
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            IT infrastructure & business-critical system outages, alerts, and resolutions.
          </p>
        </div>

        <div className="flex items-center gap-2 self-start md:self-auto">
          <div className="glass-subtle rounded-xl p-0.5 flex">
            <button
              onClick={() => setView("list")}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-all",
                view === "list" ? "bg-white/10 text-white" : "text-slate-300 hover:text-white",
              )}
              title="List view"
            >
              <List className="w-3.5 h-3.5" /> List
            </button>
            <button
              onClick={() => setView("kanban")}
              className={cn(
                "px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-all",
                view === "kanban" ? "bg-white/10 text-white" : "text-slate-300 hover:text-white",
              )}
              title="Kanban view"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Kanban
            </button>
          </div>
          <button onClick={() => setIsNewOpen(true)} className="btn-primary shadow-glow">
            <Plus className="w-4 h-4" /> New Access Issue
          </button>
        </div>
      </div>

      <GlassCard className="flex flex-col sm:flex-row items-center gap-4 py-4">
        <div className="relative w-full sm:flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search access issues..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10 bg-white/[0.03] border-white/10 hover:bg-white/[0.05] focus:bg-white/[0.07] transition-all"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <span className="text-xs uppercase tracking-wider text-slate-500 shrink-0 font-medium">Priority:</span>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="select py-2 bg-white/[0.03] border-white/10 text-xs rounded-xl"
          >
            <option value="">All Priorities</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_META[p].label}
              </option>
            ))}
          </select>
        </div>
      </GlassCard>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="glass-subtle rounded-3xl p-4 space-y-4">
              <div className="skeleton h-8 w-24 rounded-lg" />
              <div className="skeleton h-32 rounded-2xl" />
              <div className="skeleton h-32 rounded-2xl" />
            </div>
          ))}
        </div>
      ) : view === "list" ? (
        <ListView
          items={filteredItems}
          onSelect={(ticket) => {
            setDetailTicket(ticket);
            setDetailResolution(ticket.resolution || "");
            setIsDetailOpen(true);
          }}
          onResolve={(e, ticket) => {
            e.stopPropagation();
            setResolvingTicket(ticket);
            setIsResolveOpen(true);
          }}
          canModify={canModifyTicket}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4 items-start">
          {STATUSES.map((status) => {
            const list = columns[status] || [];
            const meta = STATUS_META[status];
            return (
              <div key={status} className="glass rounded-3xl p-3 flex flex-col h-[75vh]">
                <div className="flex items-center justify-between mb-3 px-1 shrink-0">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={status} />
                    <span className="text-xs font-semibold text-slate-400 bg-white/5 rounded-full px-2 py-0.5">
                      {list.length}
                    </span>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto space-y-3 pr-1 scrollbar-thin scrollbar-thumb-white/10 hover:scrollbar-thumb-white/20 pb-4">
                  {list.length === 0 ? (
                    <div className="h-24 border border-dashed border-white/5 rounded-2xl flex items-center justify-center text-xs text-slate-500 italic p-3 text-center">
                      No access issues
                    </div>
                  ) : (
                    list.map((ticket) => {
                      const priorityMeta = PRIORITY_META[ticket.priority];
                      const isResolved = ["RESOLVED", "CLOSED"].includes(ticket.status);

                      return (
                        <div
                          key={ticket.id}
                          className="group relative glass-subtle rounded-2xl p-4 border border-white/10 hover:border-brand-500/40 hover:bg-white/[0.06] transition-all duration-300 shadow-glass-sm hover:shadow-glow flex flex-col gap-2.5 cursor-pointer overflow-hidden"
                          onClick={() => {
                            setDetailTicket(ticket);
                            setDetailResolution(ticket.resolution || "");
                            setIsDetailOpen(true);
                          }}
                        >
                          <span
                            aria-hidden
                            className={cn(
                              "absolute inset-x-0 top-0 h-0.5 opacity-60 group-hover:opacity-100 transition-opacity",
                              priorityMeta.dot,
                            )}
                          />

                          <div className="flex justify-between items-center text-[10px] text-slate-400 select-none">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-slate-500" />
                              {new Date(ticket.createdAt).toLocaleDateString(undefined, {
                                month: "short",
                                day: "numeric",
                              })}
                            </span>
                            <PriorityBadge priority={ticket.priority} />
                          </div>

                          <div className="p-2 rounded-xl bg-white/[0.02] border border-white/5 flex flex-col gap-1">
                            <span className="text-[8px] uppercase tracking-wider text-slate-500 font-bold block">
                              Agent That Have Issue:
                            </span>
                            <div className="flex items-center gap-2">
                              <Avatar name={ticket.submitter?.name || "?"} url={ticket.submitter?.avatarUrl} size="xs" />
                              <span className="text-xs text-white font-semibold truncate">
                                {ticket.submitter?.name || "Unknown"}
                              </span>
                              {ticket.submitter?.department && (
                                <span className="text-[9px] text-slate-500 truncate max-w-[80px]">
                                  ({ticket.submitter.department})
                                </span>
                              )}
                            </div>
                          </div>

                          <div>
                            <h4 className="text-sm font-semibold text-white group-hover:text-brand-300 transition-colors line-clamp-1">
                              {ticket.title}
                            </h4>
                            <p className="text-xs text-slate-400 mt-1 line-clamp-2 leading-relaxed">
                              {ticket.description}
                            </p>
                          </div>

                          {ticket.resolution && (
                            <div className="p-2.5 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-emerald-300 text-xs">
                              <span className="font-semibold block mb-0.5 text-emerald-200 uppercase tracking-wider text-[9px] flex items-center gap-1 select-none">
                                <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                                Resolution
                              </span>
                              <p className="line-clamp-2 text-slate-300 leading-normal">{ticket.resolution}</p>
                            </div>
                          )}

                          {isResolved ? (
                            <div className="pt-2 border-t border-white/5 space-y-1.5 select-none">
                              {ticket.resolvedAt && (
                                <div className="text-[10px] text-slate-500 flex items-center justify-between">
                                  <span>Resolved Date:</span>
                                  <span className="text-slate-300 font-medium">
                                    {new Date(ticket.resolvedAt).toLocaleDateString(undefined, {
                                      month: "short",
                                      day: "numeric",
                                    })}
                                  </span>
                                </div>
                              )}
                              {ticket.assignee && (
                                <div className="flex items-center justify-between">
                                  <span className="text-[9px] uppercase tracking-wider text-slate-500">Resolved By:</span>
                                  <div className="flex items-center gap-1 text-[11px] text-slate-300 font-medium">
                                    <Avatar name={ticket.assignee.name} url={ticket.assignee.avatarUrl} size="xs" />
                                    <span>{truncate(ticket.assignee.name, 12)}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="pt-2 border-t border-white/5 flex items-center justify-between gap-2 mt-auto select-none">
                              {ticket.assignee ? (
                                <div className="flex items-center gap-1 text-[10px] text-slate-400">
                                  <Avatar name={ticket.assignee.name} url={ticket.assignee.avatarUrl} size="xs" />
                                  <span className="truncate max-w-[80px]">{truncate(ticket.assignee.name, 12)}</span>
                                </div>
                              ) : (
                                <span className="text-[10px] text-slate-500 italic">Unassigned</span>
                              )}

                              {canModifyTicket(ticket) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setResolvingTicket(ticket);
                                    setIsResolveOpen(true);
                                  }}
                                  className="px-2.5 py-1 bg-white/5 hover:bg-emerald-500/10 border border-white/10 hover:border-emerald-500/30 text-slate-300 hover:text-emerald-400 text-[10px] rounded-xl flex items-center gap-1 font-semibold transition-all shadow-sm"
                                >
                                  <Check className="w-3 h-3 text-slate-400 group-hover:text-emerald-400" /> Mark Resolved
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={isResolveOpen}
        onClose={() => {
          setIsResolveOpen(false);
          setResolvingTicket(null);
          setResolution("");
        }}
        title="Resolve Access Issue"
        size="md"
      >
        {resolvingTicket && (
          <form onSubmit={handleResolveSubmit} className="space-y-4">
            <div className="p-3 rounded-2xl bg-white/5 border border-white/10">
              <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block">Access Issue:</span>
              <h4 className="text-sm font-semibold text-white mt-1">{resolvingTicket.title}</h4>
              <p className="text-xs text-slate-300 mt-1 line-clamp-3 leading-relaxed">
                {resolvingTicket.description}
              </p>
            </div>

            <div>
              <label className="label">Resolution Details <span className="text-critical-400">*</span></label>
              <textarea
                className="textarea"
                rows={4}
                required
                placeholder="What was the root cause? How was it fixed? E.g., Restarted the nginx service on web-01."
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Resolving this will set its status to **Resolved**, input your name as the resolving agent, and log the action.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  setIsResolveOpen(false);
                  setResolvingTicket(null);
                  setResolution("");
                }}
              >
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={patchMut.isPending}>
                {patchMut.isPending ? <Spinner /> : <Check className="w-4 h-4" />}
                Confirm Resolution
              </button>
            </div>
          </form>
        )}
      </Modal>

      <Modal
        open={isNewOpen}
        onClose={() => setIsNewOpen(false)}
        title="New Access Issue"
        size="lg"
      >
        <form onSubmit={handleNewSubmit} className="space-y-4">
          <div>
            <label className="label">Agent That Have Issue <span className="text-critical-400">*</span></label>
            <div className="input flex items-center bg-white/5 text-slate-300 border-white/10 cursor-not-allowed select-none">
              {user?.name || "Unknown"}
            </div>
          </div>

          <div>
            <label className="label">Access Issue Title <span className="text-critical-400">*</span></label>
            <input
              className="input"
              required
              placeholder="E.g., Production Postgres DB Outage"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
          </div>

          <div>
            <label className="label">Description & Symptoms <span className="text-critical-400">*</span></label>
            <textarea
              className="textarea"
              rows={5}
              required
              placeholder="Describe the problem, error messages, and affected services..."
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Priority Level</label>
              <div className="grid grid-cols-2 gap-2">
                {PRIORITIES.map((p) => {
                  const meta = PRIORITY_META[p];
                  const active = newPriority === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setNewPriority(p)}
                      className={cn(
                        "rounded-xl px-3 py-2 text-left text-xs border transition-all",
                        active
                          ? `${meta.bg} ${meta.border} ring-1 ${meta.ring} text-white`
                          : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]",
                      )}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <span className={cn("w-1.5 h-1.5 rounded-full", meta.dot)} />
                        {meta.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="label">Category</label>
              <div className="p-3 rounded-2xl bg-brand-500/10 border border-brand-500/20 text-brand-300 flex items-center gap-2 select-none h-[42px]">
                <Key className="w-4 h-4" />
                <span className="text-xs font-semibold uppercase tracking-wider">Locked to ACCESS</span>
              </div>
              <p className="text-[10px] text-slate-500 mt-1">This tracker automatically scopes new issues to SYSTEM.</p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" className="btn-ghost" onClick={() => setIsNewOpen(false)}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={createMut.isPending}>
              {createMut.isPending ? <Spinner /> : <Plus className="w-4 h-4" />}
              Create Issue
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setDetailTicket(null);
          setDetailResolution("");
        }}
        title="Access Issue Details"
        size="xl"
      >
        {detailTicket && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
                <h3 className="text-base font-semibold text-white">{detailTicket.title}</h3>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  <PriorityBadge priority={detailTicket.priority} />
                  <StatusBadge status={detailTicket.status} />
                  <CategoryBadge category={detailTicket.category} />
                  <span className="text-[10px] text-slate-400 font-mono">
                    ID: {detailTicket.id.slice(-8).toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="p-3 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold block">Agent That Have Issue</span>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <Avatar name={detailTicket.submitter?.name || "?"} url={detailTicket.submitter?.avatarUrl} size="sm" />
                    <div>
                      <div className="text-sm font-semibold text-white">{detailTicket.submitter?.name || "Unknown"}</div>
                      <div className="text-[10px] text-slate-400">{detailTicket.submitter?.email || ""}</div>
                    </div>
                  </div>
                  {detailTicket.submitter?.department && (
                    <span className="text-xs font-semibold text-slate-300 px-3 py-1 rounded-xl bg-white/5 border border-white/10 select-none">
                      {detailTicket.submitter.department}
                    </span>
                  )}
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Description</label>
                <div className="mt-1 p-3 rounded-2xl bg-white/[0.02] border border-white/5 text-sm text-slate-300 leading-relaxed whitespace-pre-wrap max-h-[200px] overflow-y-auto custom-scrollbar">
                  {detailTicket.description}
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/10 space-y-3">
                <label className="text-xs uppercase tracking-wider text-emerald-400 font-semibold flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Resolution Details
                </label>
                <textarea
                  className="textarea text-xs bg-white/5"
                  rows={4}
                  placeholder="Enter the details of how this access issue was resolved..."
                  value={detailResolution}
                  onChange={(e) => setDetailResolution(e.target.value)}
                  disabled={!canModifyDetail}
                />
                {canModifyDetail && (
                  <button
                    onClick={handleSaveResolutionInDetail}
                    disabled={patchMut.isPending}
                    className="btn-ghost py-1.5 px-3 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs rounded-xl flex items-center gap-1.5 font-medium transition-all"
                  >
                    <Save className="w-3.5 h-3.5" /> Save Resolution
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-4 p-4 rounded-2xl bg-white/5 border border-white/10 self-start">
              <h4 className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 flex items-center gap-2">
                <Activity className="w-3.5 h-3.5" /> Controls
              </h4>

              <div className="space-y-1">
                <label className="label text-[10px] uppercase text-slate-500">Status</label>
                <select
                  value={detailTicket.status}
                  onChange={(e) => handleUpdateStatus(e.target.value)}
                  className="select text-xs py-2 bg-white/5 border-white/10"
                  disabled={!canModifyDetail}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_META[s].label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="label text-[10px] uppercase text-slate-500">Priority</label>
                <select
                  value={detailTicket.priority}
                  onChange={(e) => handleUpdatePriority(e.target.value)}
                  className="select text-xs py-2 bg-white/5 border-white/10"
                  disabled={!canModifyDetail}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_META[p].label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <label className="label text-[10px] uppercase text-slate-500">Assignee (IT Staff)</label>
                <select
                  value={detailTicket.assigneeId || ""}
                  onChange={(e) => handleUpdateAssignee(e.target.value)}
                  className="select text-xs py-2 bg-white/5 border-white/10"
                  disabled={!canModifyDetail}
                >
                  <option value="">— Unassigned —</option>
                  {(assigneesQ.data as UserSummary[] | undefined)?.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.role})
                    </option>
                  ))}
                </select>
              </div>

              <div className="pt-4 mt-4 border-t border-white/5 space-y-2 select-none">
                <div className="flex justify-between items-center text-[10px] text-slate-500">
                  <span>Created:</span>
                  <span className="text-slate-300">{fmtDateTime(detailTicket.createdAt)}</span>
                </div>
                {detailTicket.resolvedAt && (
                  <div className="flex justify-between items-center text-[10px] text-slate-500">
                    <span>Resolved:</span>
                    <span className="text-slate-300">{fmtDateTime(detailTicket.resolvedAt)}</span>
                  </div>
                )}
                <div className="pt-2 space-y-2">
                  <Link
                    to={`/tickets/${detailTicket.id}`}
                    className="w-full btn-ghost py-2 text-xs flex items-center justify-center gap-1.5 rounded-xl border border-white/10 hover:bg-white/5 transition-all text-indigo-300 hover:text-indigo-200"
                  >
                    <Eye className="w-3.5 h-3.5" /> Full Ticket Page <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                  <button
                    onClick={() => exportTicketPdf(detailTicket)}
                    className="w-full btn-ghost py-2 text-xs flex items-center justify-center gap-1.5 rounded-xl border border-white/10 hover:bg-white/5 transition-all text-slate-300 hover:text-white"
                  >
                    <Download className="w-3.5 h-3.5" /> Export PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

